import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { GET_SAVED_CHATS } from '@/hooks/query/useQuerySavedChats';
import { type ChatListItem } from '@/types';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { type ChatListResponse } from '@heidi/session/contracts';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

interface SaveChatContext {
  previousChats: ChatListResponse | undefined;
  previousSaved: ChatListResponse | undefined;
}

export function useMutateSaveChat() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, string, SaveChatContext>({
    mutationFn: async (chatId: string) => {
      await scribeHttpV2.post(`/chat/chats/${chatId}/save`);
      return chatId;
    },
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: [GET_CHATS] });
      await queryClient.cancelQueries({ queryKey: [GET_SAVED_CHATS] });

      const previousChats = queryClient.getQueryData<ChatListResponse>([
        GET_CHATS,
        1,
        100,
      ]);
      const previousSaved = queryClient.getQueryData<ChatListResponse>([
        GET_SAVED_CHATS,
        1,
        100,
      ]);

      // Find the chat from any available cache to add to saved list
      let chatToSave: ChatListItem | undefined = previousChats?.chats.find(
        (c) => c.id === chatId
      );
      if (!chatToSave) {
        const infiniteEntries = queryClient.getQueriesData<
          InfiniteData<ChatListResponse>
        >({
          predicate: (query) =>
            query.queryKey[0] === GET_CHATS && query.queryKey[1] === 'infinite',
        });
        for (const [, data] of infiniteEntries) {
          chatToSave = data?.pages.flatMap((p) => p.chats).find((c) => c.id === chatId);
          if (chatToSave) break;
        }
      }

      // Optimistically mark as saved in useQueryChats cache (drives isChatSaved)
      queryClient.setQueryData<ChatListResponse>([GET_CHATS, 1, 100], (old) => {
        if (!old) return old;
        return {
          ...old,
          chats: old.chats.map((c) => (c.id === chatId ? { ...c, is_saved: true } : c)),
        };
      });

      // Optimistically mark as saved in all infinite chat queries (star in sidebar)
      queryClient.setQueriesData<InfiniteData<ChatListResponse>>(
        {
          predicate: (query) =>
            query.queryKey[0] === GET_CHATS && query.queryKey[1] === 'infinite',
        },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              chats: page.chats.map((c) =>
                c.id === chatId ? { ...c, is_saved: true } : c
              ),
            })),
          };
        }
      );

      // Optimistically add to saved chats list
      queryClient.setQueryData<ChatListResponse>([GET_SAVED_CHATS, 1, 100], (old) => {
        if (!chatToSave) return old;
        const savedChat: ChatListItem = { ...chatToSave, is_saved: true };
        if (!old) return { chats: [savedChat], total: 1, page: 1, page_size: 100 };
        if (old.chats.some((c) => c.id === chatId)) return old;
        return { ...old, chats: [savedChat, ...old.chats], total: old.total + 1 };
      });

      return { previousChats, previousSaved };
    },
    onError: (_error, _chatId, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData([GET_CHATS, 1, 100], context.previousChats);
      }
      if (context?.previousSaved) {
        queryClient.setQueryData([GET_SAVED_CHATS, 1, 100], context.previousSaved);
      }
      logger.error({ error: _error });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_CHATS] });
      void queryClient.invalidateQueries({ queryKey: [GET_SAVED_CHATS] });
    },
  });
}

export function useMutateUnsaveChat() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, string, SaveChatContext>({
    mutationFn: async (chatId: string) => {
      await scribeHttpV2.delete(`/chat/chats/${chatId}/save`);
      return chatId;
    },
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: [GET_CHATS] });
      await queryClient.cancelQueries({ queryKey: [GET_SAVED_CHATS] });

      const previousChats = queryClient.getQueryData<ChatListResponse>([
        GET_CHATS,
        1,
        100,
      ]);
      const previousSaved = queryClient.getQueryData<ChatListResponse>([
        GET_SAVED_CHATS,
        1,
        100,
      ]);

      // Optimistically mark as unsaved in useQueryChats cache (drives isChatSaved)
      queryClient.setQueryData<ChatListResponse>([GET_CHATS, 1, 100], (old) => {
        if (!old) return old;
        return {
          ...old,
          chats: old.chats.map((c) => (c.id === chatId ? { ...c, is_saved: false } : c)),
        };
      });

      // Optimistically mark as unsaved in all infinite chat queries (star in sidebar)
      queryClient.setQueriesData<InfiniteData<ChatListResponse>>(
        {
          predicate: (query) =>
            query.queryKey[0] === GET_CHATS && query.queryKey[1] === 'infinite',
        },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              chats: page.chats.map((c) =>
                c.id === chatId ? { ...c, is_saved: false } : c
              ),
            })),
          };
        }
      );

      // Optimistically remove from saved chats list
      queryClient.setQueryData<ChatListResponse>([GET_SAVED_CHATS, 1, 100], (old) => {
        if (!old) return old;
        const filtered = old.chats.filter((c) => c.id !== chatId);
        return { ...old, chats: filtered, total: filtered.length };
      });

      return { previousChats, previousSaved };
    },
    onError: (_error, _chatId, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData([GET_CHATS, 1, 100], context.previousChats);
      }
      if (context?.previousSaved) {
        queryClient.setQueryData([GET_SAVED_CHATS, 1, 100], context.previousSaved);
      }
      logger.error({ error: _error });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_CHATS] });
      void queryClient.invalidateQueries({ queryKey: [GET_SAVED_CHATS] });
    },
  });
}
